import jsPDF from 'jspdf';
import { captureElement } from './captureElement';
import { aoiPoints } from './aoi';
import { extentOf } from './utils';

/**
 * Writes an analysis to a single PDF document.
 * Every module produces the same kinds of pieces - what area was analysed, which imagery went into
 * it, a table of metrics, a chart, a map - and today the only way to hand those to someone is a
 * screenshot of the screen they happen to be on. This renderer takes a description of a document
 * and lays it out: a module says what its sections are and what goes in them, and nothing here
 * knows anything about bathymetry, shorelines or land cover.
 * The description is plain data, so a module's part is pure and testable, and the layout - page
 * breaks, headers, footers, column widths - is solved once instead of eight times.
 * @module PDF_REPORT
 */

/**
 * @constant PAGE
 * @description Page geometry in millimetres. A4 portrait, which is what the people receiving these
 * reports print on.
 * @memberof module:PDF_REPORT
 */
export const PAGE = { width: 210, height: 297, margin: 16 };

/**
 * @constant CONTENT_WIDTH
 * @description Usable width between the margins, in mm.
 * @memberof module:PDF_REPORT
 */
export const CONTENT_WIDTH = PAGE.width - PAGE.margin * 2;

/**
 * @constant TYPE
 * @description Font sizes in points and the line height each one needs, in mm. Kept in one place
 * so a section cannot drift away from the rest of the document.
 * @memberof module:PDF_REPORT
 */
export const TYPE = {
  title: { size: 18, line: 8 },
  subtitle: { size: 10, line: 5 },
  heading: { size: 12, line: 6.5 },
  body: { size: 9, line: 4.6 },
  caption: { size: 7.5, line: 3.8 },
};

/**
 * @constant INK
 * @description Greys and the one accent, as RGB triples. A report is read on paper as often as on
 * a screen, so the palette is near-monochrome and the accent carries only the rules and headings.
 * @memberof module:PDF_REPORT
 */
export const INK = {
  text: [33, 33, 33],
  muted: [110, 110, 110],
  rule: [66, 165, 245],
  stripe: [243, 246, 249],
  border: [220, 224, 228],
};

/**
 * @function labelValueGap
 * @description Width, in mm, reserved for a key/value row's label. The value takes the rest and
 * wraps inside it, which is what keeps a long value - a list of eighteen predictor names - from
 * pushing the column off the page instead of running onto a second line.
 * @memberof module:PDF_REPORT
 * @returns {Number}
 */
const labelValueGap = () => CONTENT_WIDTH * 0.42;

/**
 * @function createCursor
 * @description The write head: where the next thing goes, and how to move down the page.
 * @param {Object} doc - The jsPDF document.
 * @memberof module:PDF_REPORT
 * @returns {Object} `{ y, newPage, ensure, advance }`.
 */
const createCursor = (doc) => {
  const state = { y: PAGE.margin, page: 1 };

  const newPage = () => {
    doc.addPage();
    state.page += 1;
    state.y = PAGE.margin;
  };

  /**
   * Asks for vertical room. A block that does not fit starts the next page rather than being cut
   * across the fold, which is the difference between a document and a long screenshot.
   */
  const ensure = (height) => {
    if (state.y + height <= PAGE.height - PAGE.margin - 10) return;
    newPage();
  };

  return {
    get y() {
      return state.y;
    },
    set y(value) {
      state.y = value;
    },
    newPage,
    ensure,
    advance: (height) => {
      state.y += height;
    },
  };
};

/**
 * @function setText
 * @description Sets size, weight and colour in one call, since every writer here needs all three.
 * @param {Object} doc - The jsPDF document.
 * @param {Object} style - One of {@link TYPE}.
 * @param {Object} [options]
 * @param {Array<Number>} [options.color] - One of {@link INK}.
 * @param {String} [options.weight] - `'normal'` or `'bold'`.
 * @memberof module:PDF_REPORT
 * @returns {void}
 */
const setText = (doc, style, { color = INK.text, weight = 'normal' } = {}) => {
  doc.setFont('helvetica', weight);
  doc.setFontSize(style.size);
  doc.setTextColor(color[0], color[1], color[2]);
};

/**
 * @function writeHeader
 * @description The document's title block, on the first page only.
 * @param {Object} doc - The jsPDF document.
 * @param {Object} cursor - From {@link createCursor}.
 * @param {Object} report - The document description.
 * @memberof module:PDF_REPORT
 * @returns {void}
 */
const writeHeader = (doc, cursor, report) => {
  setText(doc, TYPE.title, { weight: 'bold' });
  doc.text(report.title || '', PAGE.margin, cursor.y + 6);
  cursor.advance(TYPE.title.line + 1);

  if (report.subtitle) {
    setText(doc, TYPE.subtitle, { color: INK.muted });
    doc.text(report.subtitle, PAGE.margin, cursor.y + 2);
    cursor.advance(TYPE.subtitle.line);
  }

  doc.setDrawColor(INK.rule[0], INK.rule[1], INK.rule[2]);
  doc.setLineWidth(0.6);
  doc.line(PAGE.margin, cursor.y + 2, PAGE.width - PAGE.margin, cursor.y + 2);
  cursor.advance(6);
};

/**
 * @function writeHeading
 * @description A section heading with a hairline under it.
 * @param {Object} doc - The jsPDF document.
 * @param {Object} cursor - From {@link createCursor}.
 * @param {String} text - The heading.
 * @memberof module:PDF_REPORT
 * @returns {void}
 */
const writeHeading = (doc, cursor, text) => {
  cursor.ensure(TYPE.heading.line + 6);
  setText(doc, TYPE.heading, { weight: 'bold' });
  doc.text(text, PAGE.margin, cursor.y + 4);
  cursor.advance(TYPE.heading.line);
  doc.setDrawColor(INK.border[0], INK.border[1], INK.border[2]);
  doc.setLineWidth(0.2);
  doc.line(PAGE.margin, cursor.y, PAGE.width - PAGE.margin, cursor.y);
  cursor.advance(3);
};

/**
 * @function writeRows
 * @description Key/value rows, the label in grey on the left and the value beside it, wrapping
 * within its own column.
 * @param {Object} doc - The jsPDF document.
 * @param {Object} cursor - From {@link createCursor}.
 * @param {Array<Array<String>>} rows - `[label, value]` pairs. A pair with an empty value is
 *  dropped, so a module can list a field without first checking whether it has one.
 * @memberof module:PDF_REPORT
 * @returns {void}
 */
const writeRows = (doc, cursor, rows) => {
  const labelWidth = labelValueGap();
  const valueWidth = CONTENT_WIDTH - labelWidth;

  (rows || [])
    .filter((row) => Array.isArray(row) && row[1] !== null && row[1] !== undefined && String(row[1]).trim() !== '')
    .forEach(([label, value]) => {
      setText(doc, TYPE.body);
      const lines = doc.splitTextToSize(String(value), valueWidth);
      const height = Math.max(TYPE.body.line, lines.length * TYPE.body.line);
      cursor.ensure(height);

      setText(doc, TYPE.body, { color: INK.muted });
      doc.text(String(label), PAGE.margin, cursor.y + 3.2);
      setText(doc, TYPE.body);
      doc.text(lines, PAGE.margin + labelWidth, cursor.y + 3.2);
      cursor.advance(height);
    });
  cursor.advance(2);
};

/**
 * @function writeTable
 * @description A table with a header row, striped body and right-aligned numeric columns.
 * The header repeats whenever the table runs onto another page: a column of bare numbers under no
 * heading is unreadable, and these tables are long enough to break.
 * @param {Object} doc - The jsPDF document.
 * @param {Object} cursor - From {@link createCursor}.
 * @param {Object} table - `{ columns, rows, align }`, where `align` is per column, `'left'` or
 *  `'right'`, defaulting to left for the first column and right for the rest.
 * @memberof module:PDF_REPORT
 * @returns {void}
 */
const writeTable = (doc, cursor, table) => {
  const columns = table?.columns || [];
  const rows = table?.rows || [];
  if (!columns.length || !rows.length) return;

  const columnWidth = CONTENT_WIDTH / columns.length;
  const align = (index) => table?.align?.[index] || (index === 0 ? 'left' : 'right');
  const x = (index) =>
    align(index) === 'right' ? PAGE.margin + columnWidth * (index + 1) - 2 : PAGE.margin + columnWidth * index + 2;
  const rowHeight = TYPE.body.line + 1.4;

  const header = () => {
    cursor.ensure(rowHeight * 2);
    doc.setFillColor(INK.stripe[0], INK.stripe[1], INK.stripe[2]);
    doc.rect(PAGE.margin, cursor.y, CONTENT_WIDTH, rowHeight, 'F');
    setText(doc, TYPE.caption, { weight: 'bold', color: INK.muted });
    columns.forEach((column, index) => {
      doc.text(String(column), x(index), cursor.y + rowHeight - 1.8, { align: align(index) });
    });
    cursor.advance(rowHeight);
  };

  header();

  rows.forEach((row, rowIndex) => {
    if (cursor.y + rowHeight > PAGE.height - PAGE.margin - 10) {
      cursor.newPage();
      header();
    }
    if (rowIndex % 2 === 1) {
      doc.setFillColor(250, 251, 252);
      doc.rect(PAGE.margin, cursor.y, CONTENT_WIDTH, rowHeight, 'F');
    }
    setText(doc, TYPE.body);
    row.forEach((cell, index) => {
      const text = cell === null || cell === undefined || cell === '' ? '—' : String(cell);
      doc.text(text, x(index), cursor.y + rowHeight - 1.8, { align: align(index) });
    });
    cursor.advance(rowHeight);
  });

  doc.setDrawColor(INK.border[0], INK.border[1], INK.border[2]);
  doc.setLineWidth(0.2);
  doc.line(PAGE.margin, cursor.y, PAGE.width - PAGE.margin, cursor.y);
  cursor.advance(4);
};

/**
 * @function writeImages
 * @description Images laid out one or two to a row, each scaled to its column and captioned.
 * An entry that failed to load is skipped and its caption is written as a note instead, so a map
 * the browser refused to release does not cost the whole report.
 * @param {Object} doc - The jsPDF document.
 * @param {Object} cursor - From {@link createCursor}.
 * @param {Array<Object>} images - `{ dataUrl, width, height, caption }` entries.
 * @param {Function} missingNote - Given a caption, the line to write when that image is missing.
 * @memberof module:PDF_REPORT
 * @returns {void}
 */
const writeImages = (doc, cursor, images, missingNote) => {
  const usable = (images || []).filter((image) => image?.dataUrl);
  const missing = (images || []).filter((image) => image && !image.dataUrl);

  const perRow = usable.length > 1 ? 2 : 1;
  const gap = 5;
  const columnWidth = (CONTENT_WIDTH - gap * (perRow - 1)) / perRow;

  for (let index = 0; index < usable.length; index += perRow) {
    const row = usable.slice(index, index + perRow);
    const heights = row.map((image) => {
      const ratio = image.height && image.width ? image.height / image.width : 0.75;
      return columnWidth * ratio;
    });
    const tallest = Math.max(...heights);
    cursor.ensure(tallest + TYPE.caption.line + 4);

    row.forEach((image, column) => {
      const left = PAGE.margin + (columnWidth + gap) * column;
      doc.addImage(image.dataUrl, 'JPEG', left, cursor.y, columnWidth, heights[column]);
      doc.setDrawColor(INK.border[0], INK.border[1], INK.border[2]);
      doc.setLineWidth(0.2);
      doc.rect(left, cursor.y, columnWidth, heights[column]);
      if (image.caption) {
        setText(doc, TYPE.caption, { color: INK.muted });
        doc.text(doc.splitTextToSize(image.caption, columnWidth), left, cursor.y + tallest + 3.4);
      }
    });
    cursor.advance(tallest + TYPE.caption.line + 4);
  }

  if (missing.length && missingNote) {
    setText(doc, TYPE.caption, { color: INK.muted });
    missing.forEach((image) => {
      cursor.ensure(TYPE.caption.line);
      doc.text(doc.splitTextToSize(missingNote(image.caption), CONTENT_WIDTH), PAGE.margin, cursor.y + 2.6);
      cursor.advance(TYPE.caption.line);
    });
    cursor.advance(2);
  }
};

/**
 * @constant CHART_HEIGHT
 * @description Height of a chart's plotting area, in mm. Tall enough to read a trend off, short
 * enough that the chart and the table it belongs to can share a page.
 * @memberof module:PDF_REPORT
 */
export const CHART_HEIGHT = 52;

/**
 * @function niceStep
 * @description A round interval for an axis: 1, 2, 5 or 10 times a power of ten, whichever gives
 * about the number of gridlines asked for. Axis labels of 0, 2.5, 5 read; labels of 0, 2.37, 4.74
 * do not.
 * @param {Number} span - The range the axis has to cover.
 * @param {Number} [target=5] - Roughly how many intervals to aim for.
 * @memberof module:PDF_REPORT
 * @returns {Number}
 */
export const niceStep = (span, target = 5) => {
  if (!Number.isFinite(span) || span <= 0) return 1;
  const raw = span / target;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const normalised = raw / magnitude;
  const step = normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10;
  return step * magnitude;
};

/**
 * @function writeChart
 * @description A line chart with a point at each value, drawn as vector rather than as an image.
 * This is the shape of a result that a table states but does not show: where the model is reliable
 * and where it stops being so is a curve, and a reader finds the turn in it far faster than by
 * comparing rows. Drawing it here rather than rendering a chart library to a canvas keeps it sharp
 * at any zoom and keeps the page small.
 * A series with fewer than two points is skipped: a single dot is not a trend, and an empty frame
 * suggests something failed rather than that there was nothing to plot.
 * @param {Object} doc - The jsPDF document.
 * @param {Object} cursor - The write head.
 * @param {Object} chart - `{ points: [{ x, y }], xLabel, yLabel, caption }`.
 * @memberof module:PDF_REPORT
 * @returns {void}
 */
const writeChart = (doc, cursor, chart) => {
  const points = (chart?.points || []).filter(
    (point) => Number.isFinite(Number(point?.x)) && Number.isFinite(Number(point?.y)),
  );
  if (points.length < 2) return;

  const left = PAGE.margin + 14;
  const width = CONTENT_WIDTH - 16;
  cursor.ensure(CHART_HEIGHT + 18);
  const top = cursor.y + 2;
  const bottom = top + CHART_HEIGHT;

  const xs = points.map((point) => Number(point.x));
  const ys = points.map((point) => Number(point.y));
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  /** The y axis starts at zero: an error chart that does not is a chart that exaggerates. */
  const yMax = Math.max(...ys, 0);
  const yStep = niceStep(yMax || 1);
  const yTop = Math.ceil((yMax || 1) / yStep) * yStep;
  const xStep = niceStep(xMax - xMin || 1);

  const toX = (value) => left + ((value - xMin) / (xMax - xMin || 1)) * width;
  const toY = (value) => bottom - (value / (yTop || 1)) * CHART_HEIGHT;

  doc.setDrawColor(INK.border[0], INK.border[1], INK.border[2]);
  doc.setLineWidth(0.15);
  setText(doc, TYPE.caption, { color: INK.muted });
  for (let value = 0; value <= yTop + 1e-9; value += yStep) {
    const y = toY(value);
    doc.line(left, y, left + width, y);
    doc.text(String(Number(value.toFixed(2))), left - 2, y + 1, { align: 'right' });
  }
  for (let value = Math.ceil(xMin / xStep) * xStep; value <= xMax + 1e-9; value += xStep) {
    doc.text(String(Number(value.toFixed(2))), toX(value), bottom + 3.6, { align: 'center' });
  }

  doc.setDrawColor(INK.muted[0], INK.muted[1], INK.muted[2]);
  doc.setLineWidth(0.3);
  doc.line(left, top, left, bottom);
  doc.line(left, bottom, left + width, bottom);

  doc.setDrawColor(66, 133, 244);
  doc.setLineWidth(0.6);
  points
    .slice()
    .sort((a, b) => Number(a.x) - Number(b.x))
    .forEach((point, index, sorted) => {
      if (index === 0) return;
      const previous = sorted[index - 1];
      doc.line(toX(Number(previous.x)), toY(Number(previous.y)), toX(Number(point.x)), toY(Number(point.y)));
    });
  doc.setFillColor(66, 133, 244);
  points.forEach((point) => doc.circle(toX(Number(point.x)), toY(Number(point.y)), 0.8, 'F'));

  setText(doc, TYPE.caption, { color: INK.muted });
  if (chart.xLabel) doc.text(chart.xLabel, left + width / 2, bottom + 8, { align: 'center' });
  if (chart.yLabel) doc.text(chart.yLabel, PAGE.margin - 2, top - 1.5);
  cursor.advance(CHART_HEIGHT + (chart.xLabel ? 12 : 7));
  if (chart.caption) {
    const lines = doc.splitTextToSize(chart.caption, CONTENT_WIDTH);
    cursor.ensure(lines.length * TYPE.caption.line);
    doc.text(lines, PAGE.margin, cursor.y);
    cursor.advance(lines.length * TYPE.caption.line + 2);
  }
};

/**
 * @function writeNote
 * @description A paragraph of prose, for a caveat that belongs with a section rather than in it.
 * @param {Object} doc - The jsPDF document.
 * @param {Object} cursor - From {@link createCursor}.
 * @param {String} text - The paragraph.
 * @memberof module:PDF_REPORT
 * @returns {void}
 */
const writeNote = (doc, cursor, text) => {
  setText(doc, TYPE.caption, { color: INK.muted });
  const lines = doc.splitTextToSize(String(text), CONTENT_WIDTH);
  cursor.ensure(lines.length * TYPE.caption.line);
  doc.text(lines, PAGE.margin, cursor.y + 2.6);
  cursor.advance(lines.length * TYPE.caption.line + 2);
};

/**
 * @function writeFooters
 * @description The footer on every page, written last because the page count is only known then.
 * @param {Object} doc - The jsPDF document.
 * @param {Object} report - The document description, for `footer`.
 * @memberof module:PDF_REPORT
 * @returns {void}
 */
const writeFooters = (doc, report) => {
  const total = doc.internal.getNumberOfPages();
  for (let page = 1; page <= total; page += 1) {
    doc.setPage(page);
    setText(doc, TYPE.caption, { color: INK.muted });
    doc.setDrawColor(INK.border[0], INK.border[1], INK.border[2]);
    doc.setLineWidth(0.2);
    doc.line(PAGE.margin, PAGE.height - PAGE.margin - 5, PAGE.width - PAGE.margin, PAGE.height - PAGE.margin - 5);
    if (report.footer) doc.text(String(report.footer), PAGE.margin, PAGE.height - PAGE.margin);
    doc.text(`${page}/${total}`, PAGE.width - PAGE.margin, PAGE.height - PAGE.margin, { align: 'right' });
  }
};

/**
 * @function renderReport
 * @description Lays a document description out as a PDF.
 * A section may carry `rows`, a `table`, a `chart`, `images` and a `note`, in that order, and any
 * of them may be absent. A section with nothing in it is skipped rather than leaving a heading over blank
 * paper, which is what happens when a module reports a stage it did not run.
 * @param {Object} report - `{ title, subtitle, footer, sections, missingImageNote }`.
 * **Called by:
 * @see {exportBathymetryReport}
 * @memberof module:PDF_REPORT
 * @returns {Object} The jsPDF document, ready to be saved.
 */
export const renderReport = (report) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', compress: true });
  const cursor = createCursor(doc);

  writeHeader(doc, cursor, report);

  (report.sections || [])
    .filter(
      (section) =>
        section && (section.rows?.length || section.table?.rows?.length || section.images?.length || section.note),
    )
    .forEach((section) => {
      if (section.heading) writeHeading(doc, cursor, section.heading);
      if (section.rows?.length) writeRows(doc, cursor, section.rows);
      if (section.table?.rows?.length) writeTable(doc, cursor, section.table);
      if (section.chart?.points?.length) writeChart(doc, cursor, section.chart);
      if (section.images?.length) writeImages(doc, cursor, section.images, report.missingImageNote);
      if (section.note) writeNote(doc, cursor, section.note);
    });

  writeFooters(doc, report);
  return doc;
};

/**
 * @function loadImageForReport
 * @description Reads an image URL into something the PDF can embed.
 * The maps in these reports are Earth Engine thumbnails, served from another origin, so the image
 * is requested with CORS and drawn to a canvas to get its bytes. A browser that refuses to release
 * it resolves to `null` rather than rejecting: a missing map is a gap in one section, while a
 * rejection would lose the whole document.
 * @param {String} url - The image URL.
 * @param {String} [caption] - Carried through to the layout.
 * @memberof module:PDF_REPORT
 * @returns {Promise<Object|null>} `{ dataUrl, width, height, caption }`, or `{ caption }` alone
 *  when the image could not be read.
 */
export const loadImageForReport = (url, caption) =>
  new Promise((resolve) => {
    if (!url) {
      resolve({ caption });
      return;
    }
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        const context = canvas.getContext('2d');
        /**
         * Painted white first: these thumbnails are PNGs with transparency outside the analysed
         * area, and JPEG has no alpha, so without this the margins come out black.
         */
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0);
        resolve({
          dataUrl: canvas.toDataURL('image/jpeg', 0.92),
          width: canvas.width,
          height: canvas.height,
          caption,
        });
      } catch (error) {
        console.error('[pdfReport] could not read an image for the report', error);
        resolve({ caption });
      }
    };
    image.onerror = () => resolve({ caption });
    image.src = url;
  });

/**
 * @function formatCoordinate
 * @description A longitude or latitude at the precision a report needs, which is metres rather
 * than millimetres.
 * @param {Number} value - The coordinate.
 * @memberof module:PDF_REPORT
 * @returns {String}
 */
const formatCoordinate = (value) => (Number.isFinite(Number(value)) ? Number(value).toFixed(5) : '-');

/**
 * @function boundingBox
 * @description The extent of a ring, as a readable pair of corners. Every module's report opens
 * with where the analysis was, and every module holds that as a ring of lon/lat pairs - or, for an
 * area read from a KML of several polygons, as those polygons.
 * @param {Array} [ring] - `[longitude, latitude]` pairs, or MultiPolygon coordinates.
 * @memberof module:PDF_REPORT
 * @returns {String|null} `null` when there is no usable ring, so the row is dropped rather than
 *  printed empty.
 */
export const boundingBox = (ring) => {
  const points = aoiPoints(ring).filter(
    (pair) => Array.isArray(pair) && Number.isFinite(Number(pair[0])) && Number.isFinite(Number(pair[1])),
  );
  if (!points.length) return null;
  const [west, east] = extentOf(points.map((pair) => Number(pair[0])));
  const [south, north] = extentOf(points.map((pair) => Number(pair[1])));
  return `${formatCoordinate(west)}, ${formatCoordinate(south)} -> ${formatCoordinate(east)}, ${formatCoordinate(north)}`;
};

/**
 * @function captureImageForReport
 * @description Renders a part of the live page into something the PDF can embed.
 * The map modules have no thumbnail to fetch - their result is a Google Maps overlay, not an image
 * with a URL - so the only way to put the map in the report is to render what is on screen. This
 * is the same capture the screenshot button takes, through the same shared function, which is why
 * the map in a report looks like the map the user was shown.
 * Resolves to a caption-only entry on any failure, for the same reason
 * {@link loadImageForReport} does: a missing map is a gap in one section, a rejection loses the
 * whole document.
 * @param {String} selector - CSS selector for the element to capture.
 * @param {String} [caption] - Carried through to the layout.
 * @memberof module:PDF_REPORT
 * @returns {Promise<Object>} `{ dataUrl, width, height, caption }`, or `{ caption }` alone.
 */
export const captureImageForReport = async (selector, caption) => {
  try {
    const element = document.querySelector(selector);
    if (!element) return { caption };
    const canvas = await captureElement(element);
    if (!canvas?.width || !canvas?.height) return { caption };
    return {
      dataUrl: canvas.toDataURL('image/jpeg', 0.9),
      width: canvas.width,
      height: canvas.height,
      caption,
    };
  } catch (error) {
    console.error('[pdfReport] could not capture an element for the report', error);
    return { caption };
  }
};

/**
 * @function reportFileName
 * @description A file name carrying the module and the day, matching how the other exports name
 * their files.
 * @param {String} module - Module key, e.g. `'bathymetry'`.
 * @memberof module:PDF_REPORT
 * @returns {String}
 */
export const reportFileName = (module) => `cassie-${module}-${new Date().toISOString().slice(0, 10)}.pdf`;
